// Catalog item ID shared by every product feed and every tracking event.
//
// 2026-09-22: GA4 `item_id` (which GTM maps to Meta Pixel/CAPI `content_ids`)
// used to be `shopItem.id`, which is the product's display NAME
// ("Upgradable Torque uDFP20 Wheel Base 9Nm"). Nothing in any catalog is keyed
// by that, so Meta matched only ~55% of item events to its catalog and Google
// Ads remarketing matched none of Merchant Center. Everything now keys on the
// backend itemType, the same offerId Merchant Center 586888161 uses (see
// ~/wire/stock_guard.py offer_id) and the id column of /api/feeds/meta.
//
// Merchant API caps offer ids at 50 chars, so longer itemTypes are truncated to
// 41 chars + '-' + the first 8 hex of sha1(itemType). This must stay
// byte-identical to the GMC push, or Google item matching breaks again.

const MAX_LEN = 50

export function catalogItemId(itemType) {
  if (!itemType) return undefined
  const t = String(itemType)
  return t.length <= MAX_LEN ? t : `${t.slice(0, 41)}-${sha1Hex(t).slice(0, 8)}`
}

// The catalog id for a shopItem, falling back to its legacy id only when the
// backend sent no itemType (never seen in practice, but never emit undefined).
export function catalogIdForShopItem(shopItem) {
  if (!shopItem) return undefined
  const itemType = shopItem.type || (shopItem.itemType && shopItem.itemType.itemType) || shopItem.itemType
  return catalogItemId(typeof itemType === 'string' ? itemType : undefined) || shopItem.id
}

// Minimal synchronous SHA-1 (hex) for ASCII/UTF-8 strings. Runs in the browser
// and in Node without crypto imports; inputs are short itemType strings.
function sha1Hex(str) {
  const bytes = unescape(encodeURIComponent(str))
  const words = []
  for (let i = 0; i < bytes.length; i++) {
    words[i >> 2] |= bytes.charCodeAt(i) << (24 - (i % 4) * 8)
  }
  const bitLen = bytes.length * 8
  words[bitLen >> 5] |= 0x80 << (24 - (bitLen % 32))
  words[(((bitLen + 64) >> 9) << 4) + 15] = bitLen

  let h0 = 0x67452301
  let h1 = 0xefcdab89
  let h2 = 0x98badcfe
  let h3 = 0x10325476
  let h4 = 0xc3d2e1f0
  const w = new Array(80)
  const rotl = (x, n) => (x << n) | (x >>> (32 - n))

  for (let i = 0; i < words.length; i += 16) {
    let a = h0
    let b = h1
    let c = h2
    let d = h3
    let e = h4
    for (let j = 0; j < 80; j++) {
      w[j] = j < 16 ? words[i + j] | 0 : rotl(w[j - 3] ^ w[j - 8] ^ w[j - 14] ^ w[j - 16], 1)
      let f
      let k
      if (j < 20) {
        f = (b & c) | (~b & d)
        k = 0x5a827999
      } else if (j < 40) {
        f = b ^ c ^ d
        k = 0x6ed9eba1
      } else if (j < 60) {
        f = (b & c) | (b & d) | (c & d)
        k = 0x8f1bbcdc
      } else {
        f = b ^ c ^ d
        k = 0xca62c1d6
      }
      const temp = (rotl(a, 5) + f + e + k + w[j]) | 0
      e = d
      d = c
      c = rotl(b, 30)
      b = a
      a = temp
    }
    h0 = (h0 + a) | 0
    h1 = (h1 + b) | 0
    h2 = (h2 + c) | 0
    h3 = (h3 + d) | 0
    h4 = (h4 + e) | 0
  }
  return [h0, h1, h2, h3, h4].map((h) => (h >>> 0).toString(16).padStart(8, '0')).join('')
}
